import { get, patch, put, uploadFile, post, deleteApi } from "../../api/index";
import store from "../../store";

import { API_ENDPOINTS, DOCUMENTS } from "../../constants";
import {
  getContentForComponent,
  getComponentPosition,
} from "../../utils/documentUtils";

import { formatClauseOption, getDynamicFieldIDs } from "./templateMethod";
import {
  updateDocSaveIndicator,
  updateComponentsDBRowColIndexesAction,
} from "../DocumentActions/method";

const { APIS } = API_ENDPOINTS;

const { DOCUMENT_MODE } = DOCUMENTS;

export const fetchDocumentTemplates = (pageNum = 1, withHeaders = false) =>
  new Promise((resolve, reject) => {
    let query = "";
    if (pageNum && withHeaders) {
      query = `?page_num=${pageNum}`;
    }
    get(`${APIS.DOCUMENT_TEMPLATES}${query}`, withHeaders)
      .then(response => {
        if (withHeaders) {
          resolve(response);
        } else {
          resolve(response.Templates);
        }
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocTemplateById = (
  typeId,
  pageNum = 1,
  withHeaders = false,
) =>
  new Promise((resolve, reject) => {
    let query = "?status=ACTIVE";
    if (typeId) {
      query += `&type_id=${typeId}`;
    }
    if (pageNum && withHeaders) {
      query += `&page_num=${pageNum}`;
    }
    get(`${APIS.DOCUMENT_TEMPLATES}${query}`, withHeaders)
      .then(response => {
        if (withHeaders) {
          resolve(response);
        } else {
          resolve(response.Templates);
        }
      })
      .catch(error => {
        reject(error);
      });
  });

export const createTemplate = (templateDetails, docDetails) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("doc", docDetails);
    formData.append("data", JSON.stringify(templateDetails));
    uploadFile(`${APIS.DOCUMENT_TEMPLATES}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchQuotetermTemplateByID = templateID =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOCUMENT_TEMPLATES}${templateID}`)
      .then(response => {
        if (!response.Template) {
          reject(new Error("Template unavailable for given id"));
        }
        resolve(response.Template);
      })
      .catch(error => {
        reject(error);
      });
  });

export const switchTemplateSections = templateID =>
  new Promise((resolve, reject) => {
    const content = {
      id: templateID,
    };
    post(`${APIS.TOGGLE_DOCUMENT_TEMPLATE}/${templateID}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteTemplateById = templateID =>
  new Promise((resolve, reject) => {
    const content = {
      id: templateID,
    };
    deleteApi(`${APIS.DELETE_DOCUMENT_TEMPLATE}/${templateID}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const saveClauseInformation = (clauseName, clauseID, clauseContent) =>
  new Promise((resolve, reject) => {
    const content = {
      name: clauseName,
      ID: clauseID,
      content: {
        clauses: clauseContent,
      },
    };
    post(`${APIS.DOCUMENT_CLAUSE}`, content)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchClauses = clauseIDs =>
  new Promise((resolve, reject) => {
    const clauseQuery = clauseIDs.map(clause => `id=${clause}`).join("&");
    get(`${APIS.DOCUMENT_CLAUSE}?${clauseQuery}`)
      .then(response => {
        resolve(response.Clauses);
      })
      .catch(error => {
        reject(error);
      });
  });

export const addTemplateComponentService = (templateID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    post(`${APIS.TEMPLATE_V2}/${templateID}/components`, {
      component_displacements: component,
      field_ids: null,
    })
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const deleteTemplateComponentService = (templateID, component) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    // get old section
    const state = store.getState();
    const { components } = state.DocumentStore;
    const componentId = component.id;
    let oldFormFieldIDs = [];
    let newFormFieldIDs = [];

    // get all dynamic fields for old section list
    oldFormFieldIDs = getDynamicFieldIDs({
      ...components,
      componentId: {
        content: component,
      },
    });

    // get all dynamic fields for current section list
    newFormFieldIDs = getDynamicFieldIDs({ ...components, componentId: {} });

    const deleted = new Set(
      [...oldFormFieldIDs].filter(id => !newFormFieldIDs.has(id)),
    );

    // request payload
    const content = {
      id: componentId,
      field_ids: {
        deleted: [...deleted],
      },
    };

    deleteApi(`${APIS.TEMPLATE_V2}/${templateID}/components`, content)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        resolve(res);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const restoreTemplateComponentService = (
  templateID,
  component,
  layout,
) => {
  const { rowIndex, colIndex } = getComponentPosition(component.id, layout);
  if (!(rowIndex === undefined || colIndex === undefined)) {
    const componentPayload = {
      component: {
        content: getContentForComponent(component, DOCUMENT_MODE.DOCUMENT),
        row: rowIndex,
        col: colIndex,
        component_id: component.id,
      },
      new_row_added: layout[rowIndex].length === 1,
    };
    return new Promise((resolve, reject) => {
      put(
        `${APIS.TEMPLATE_V2}/${templateID}/component_restore`,
        componentPayload,
      )
        .then(res => {
          if (Array.isArray(res) && res.length > 0) {
            store.dispatch(updateComponentsDBRowColIndexesAction(res));
          }
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
  return null;
};

export const updateTemplateComponentService = section =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));

    // get old section
    const state = store.getState();
    // const {
    //   template: { fields_used: fieldsUsed },
    // } = state.TemplateStore;
    const { components } = state.DocumentStore;

    // const newSection = getComponentHeader(section, fieldsUSed || []);
    const newComponent = getContentForComponent(
      section,
      DOCUMENT_MODE.TEMPLATE,
    );
    newComponent.section_id = section.id;
    newComponent.section_type = section.type || section.section_type;
    newComponent.section_name = section.header || section.section_name;
    newComponent.source = "DRAFT-JS";

    let oldFormFieldIDs = [];
    let newFormFieldIDs = [];

    // get all dynamic fields for old section list
    oldFormFieldIDs = section.oldFormFields || getDynamicFieldIDs(components);
    delete section.oldFormFields;

    // get all dynamic fields for current section list
    newFormFieldIDs = getDynamicFieldIDs({
      ...components,
      [section.id]: { ...components[section.id], content: section },
    });

    const added = new Set(
      [...newFormFieldIDs].filter(id => !oldFormFieldIDs.has(id)),
    );
    const deleted = new Set(
      [...oldFormFieldIDs].filter(id => !newFormFieldIDs.has(id)),
    );

    // request payload
    const updateComponentDetails = {
      component: {
        content: newComponent,
      },
      field_ids: {
        added: [...added],
        deleted: [...deleted],
      },
    };

    const componentURL = `${APIS.TEMPLATE_COMPONENTS}/${newComponent.id}`;

    put(componentURL, updateComponentDetails)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Component);
      })
      .catch(err => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(err);
      });
  });

export const saveTemplateComponent = templateID =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    // const rawSections = getContentForDB(content.sections, "TEMPLATE");
    // const newContent = {
    //   content: {
    //     schema_version: DOCUMENTS.DOCUMENT_SCHEMA_VERSION,
    //     sections: rawSections,
    //   },
    // };
    const state = store.getState();
    const { templateIdOrder } = state.TemplateStore;
    const content = {
      order: templateIdOrder,
    };
    post(`${APIS.TEMPLATE_COMPONENTS}/templates/${templateID}/`, content)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const updateTemplateOrder = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    const state = store.getState();
    const { template } = state.TemplateStore;

    if (!(template && payload)) {
      reject(Error("No document available, or incorrect order"));
    }
    if (!payload.old_loc) {
      const { components } = state.DocumentStore;
      const component = components[payload.component.id];
      payload = {
        ...payload,
        old_loc: {
          row: component.dbRowIndex,
          col: component.dbColIndex,
        },
      };
    }
    put(`${APIS.TEMPLATE_V2}/${template.id}/component_layout`, payload)
      .then(res => {
        if (Array.isArray(res) && res.length > 0) {
          store.dispatch(updateComponentsDBRowColIndexesAction(res));
        }
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(payload.old_loc);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const fetchDynamicFields = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_FIELDS}`)
      .then(res => {
        resolve(res.fields);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocCustomField = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.DOC_FIELDS_TYPES}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createCustomDynamicField = data =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_FIELDS}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateCustomDynamicField = (id, data) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOC_FIELDS}${id}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createClauseOptions = clause =>
  new Promise((resolve, reject) => {
    const clauseOption = formatClauseOption(clause);
    post(`${APIS.CLAUSE_OPTIONS}`, clauseOption)
      .then(res => resolve(res.field))
      .catch(err => reject(err.response));
  });

export const updateClauseOptionsService = clause =>
  new Promise((resolve, reject) => {
    const clauseOption = formatClauseOption(clause);
    put(`${APIS.CLAUSE_OPTIONS}${clause.id}`, clauseOption)
      .then(res => resolve(res.field))
      .catch(err => reject(err.response));
  });

export const updateTemplateSectionLabelService = (id, label) =>
  new Promise((resolve, reject) => {
    const state = store.getState();
    const {
      template: {
        content: { sections },
      },
    } = state.TemplateStore;
    const componentDetails = sections.find(section => section.id === id);
    componentDetails.label = label;
    updateTemplateComponentService(componentDetails)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

// Template workflow related services

export const createTempWorkFlowService = payload =>
  new Promise((resolve, reject) => {
    post(APIS.WORK_FLOW, payload)
      .then(response => {
        resolve(response.Workflow);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateTemplateWFConditionService = (id, payload) =>
  new Promise((resolve, reject) => {
    put(`${APIS.WORK_FLOW}/${id}/rules`, payload)
      .then(response => {
        resolve(response.rules);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTempWorkFlowDetailsService = id =>
  new Promise((resolve, reject) => {
    get(`${APIS.WORK_FLOW}/${id}`)
      .then(response => {
        resolve(response.Workflow);
      })
      .catch(error => {
        reject(error);
      });
  });

export const removeRuleFromTemplateWFService = ruleId =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.WORK_FLOW_RULES}/${ruleId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const removeWorkFlowFromTemplateService = workflowId =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.WORK_FLOW}/${workflowId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const cloneTemplateService = (id, name) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENT_TEMPLATES}${id}/clone`, { template_name: name })
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const changeTemplateStatusService = (id, payload) =>
  new Promise((resolve, reject) => {
    put(`${APIS.DOCUMENT_TEMPLATES}${id}`, payload)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });

export const updateTemplateTitle = (id, payload) =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    patch(`${APIS.DOCUMENT_TEMPLATES}${id}`, payload)
      .then(response => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(response);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        reject(error);
      });
  });

export const updateTemplateDetailsService = payload =>
  new Promise((resolve, reject) => {
    store.dispatch(updateDocSaveIndicator("SAVING"));
    patch(`${APIS.DOCUMENT_TEMPLATES}${payload.docId}`, payload.data)
      .then(res => {
        store.dispatch(updateDocSaveIndicator("SAVED"));
        resolve(res.Template);
      })
      .catch(error => {
        store.dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.error(
          `DocumentActions => services => updateTemplateDetailsService => ${error}`,
        );
        reject(error);
      });
  });

export const updatetemplateTagService = (templateId, tagId) =>
  new Promise((resolve, reject) => {
    put(`${APIS.TEMPLATE_TAG}/${templateId}/tag/${tagId}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

/*
==========================================
        Global Templates Services
==========================================
*/
export const getGlobalTemplates = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.GLOBAL_TEMPLATES}`)
      .then(response => {
        resolve(response && response.Templates);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchDocJSONService = globalTemplate =>
  new Promise((resolve, reject) => {
    post(`${APIS.GLOBAL_TEMPLATES_DEMO}/${globalTemplate}/docs`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        console.debug(
          `templateServices => fetchDocJSONService => Error : ${error}`,
        );
        reject(error);
      });
  });

export const createDocFromGlobalTemplates = templateID =>
  new Promise((resolve, reject) => {
    post(`${APIS.CREATE_NEW_DOCUMENT_FROM_GLOBAL_TEMPLATE}/${templateID}/docs`)
      .then(response => {
        resolve(response && response.Document);
      })
      .catch(error => {
        reject(error);
      });
  });

export const cloneGlobalTemplate = templateID =>
  new Promise((resolve, reject) => {
    post(`${APIS.CLONE_GLOBAL_TEMPLATE}/${templateID}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTemplateSearchSuggestions = (keyword, global) =>
  new Promise((resolve, reject) => {
    get(
      `${
        APIS.TEMPLATE_SEARCH_SUGGESTIONS
      }?search=${keyword}&global=${global.toString()}`,
    )
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTemplateSearchContent = (keyword, global, page = 0) =>
  new Promise((resolve, reject) => {
    get(
      `${
        APIS.TEMPLATE_SEARCH_CONTENT
      }?search=${keyword}&global=${global.toString()}&page=${page}`,
    )
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const generatePermaLinkTemplateService = payload =>
  new Promise((resolve, reject) => {
    const blob = new Blob([payload.file], { type: "plain/html" });
    const formData = new FormData();
    formData.append("template_id", payload.template_id);
    formData.append("file", blob, payload.fileName);
    formData.append("css", payload.css);
    uploadFile(`${APIS.TEMPLATES_PUBLISH}`, formData)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const deleteStripeFromTemplate = (stripeData, tempID) =>
  new Promise((resolve, reject) => {
    deleteApi(`${APIS.STRIPE_AUTH}/temp-buttons/${tempID}`, stripeData)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });

export const saveDocumentAsTemplateService = (docId, templateName) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOC_AS_TEMPLATE}/${docId}/as-temlpate`, templateName)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `saveDocumentAsTemplateService => saveDocumentAsTemplateService=> ${error}`,
        );
        reject(error);
      });
  });

export const batchUpdateComponentTemplateService = (docId, components) =>
  new Promise((resolve, reject) => {
    put(
      `${APIS.BATCH_UPDATE_TEMPLATE}/${docId}/template-components`,
      components,
    )
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `batchUpdateComponentTemplateService => batchUpdateComponentTemplateService=> ${error}`,
        );
        reject(error);
      });
  });

export const assignUserGroupToTemplateService = (templateId, data) =>
  new Promise((resolve, reject) => {
    post(`${APIS.DOCUMENT_TEMPLATES}${templateId}/usergroups`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(
          `assignUserGroupToTemplateService => assignUserGroupToTemplateService=> ${error}`,
        );
        reject(error);
      });
  });



// WEBPACK FOOTER //
// ./src/actions/DocumentTemplateActions/templateService.js